import {useEffect, useLayoutEffect, useReducer, useRef, useState} from 'react'

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const SLIDE_OUT_DURATION = 300
const REPOSITION_DURATION = 400
const SLIDE_IN_DURATION = 350
const SLIDE_DISTANCE = 32
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
const DEFAULT_SETTLE_DELAY = 200

type CardPosition = {left: number; top: number; width: number; height: number}

export type SlideCard<T> = {id: string; item: T; isSlidingOut: boolean}

type CardSlideOptions = {
  // While true, the current cards stay on screen and list changes are held back
  isLoading?: boolean
  settleDelay?: number
}

const canAnimate = () =>
  typeof window !== 'undefined' &&
  typeof Element !== 'undefined' &&
  'animate' in Element.prototype &&
  !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

const getPosition = (element: HTMLElement): CardPosition => ({
  left: element.offsetLeft,
  top: element.offsetTop,
  width: element.offsetWidth,
  height: element.offsetHeight
})

const slideOut = (element: HTMLElement, {left, top, width, height}: CardPosition) => {
  // Pin the card where it was so the rest of the grid can reflow around it
  Object.assign(element.style, {
    position: 'absolute',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    pointerEvents: 'none'
  })
  return element.animate(
    [
      {opacity: 1, transform: 'translateX(0)'},
      {opacity: 0, transform: `translateX(-${SLIDE_DISTANCE}px)`}
    ],
    {duration: SLIDE_OUT_DURATION, easing: EASING, fill: 'forwards'}
  )
}

const slideToNewPosition = (element: HTMLElement, oldPosition: CardPosition, delay: number) =>
  element.animate(
    [
      {
        transform: `translate(${oldPosition.left - element.offsetLeft}px, ${oldPosition.top - element.offsetTop}px)`
      },
      {transform: 'translate(0, 0)'}
    ],
    {duration: REPOSITION_DURATION, delay, easing: EASING, fill: 'backwards'}
  )

const slideIn = (element: HTMLElement, delay: number) =>
  element.animate(
    [
      {opacity: 0, transform: `translateX(-${SLIDE_DISTANCE}px)`},
      {opacity: 1, transform: 'translateX(0)'}
    ],
    {duration: SLIDE_IN_DURATION, delay, easing: EASING, fill: 'backwards'}
  )

// Only updates the shown card ids once loading is done and the ids have stopped changing
const useSettledCardIds = (cardIds: string[], isLoading: boolean, settleDelay: number) => {
  const [settledIds, setSettledIds] = useState(cardIds)
  const cardIdsSignature = cardIds.join('|')
  const settledIdsSignature = settledIds.join('|')

  useEffect(() => {
    if (isLoading || cardIdsSignature === settledIdsSignature) return
    if (!settledIds.length) {
      setSettledIds(cardIds)
      return
    }
    const timer = setTimeout(() => setSettledIds(cardIds), settleDelay)
    return () => clearTimeout(timer)
  }, [isLoading, cardIdsSignature, settledIdsSignature, settleDelay])

  return settledIds
}

// Puts removed cards back next to their old neighbour so they can slide out in place
const addSlidingOutCards = <T>(
  cards: SlideCard<T>[],
  lastRenderedCards: SlideCard<T>[],
  finishedSlideOutIds: Set<string>
) => {
  const result = [...cards]
  const currentIds = new Set(cards.map(({id}) => id))
  let previousNeighbourId: string | undefined

  lastRenderedCards.forEach((card) => {
    if (currentIds.has(card.id)) {
      previousNeighbourId = card.id
      return
    }
    if (finishedSlideOutIds.has(card.id)) return

    const insertAt = result.findIndex(({id}) => id === previousNeighbourId) + 1
    result.splice(insertAt, 0, {...card, isSlidingOut: true})
    previousNeighbourId = card.id
  })

  return result
}

/**
 * Removed cards slide out, remaining cards slide into their new positions, then new cards slide in.
 * Render `cards` as direct children of `containerRef` with a `data-card-slide-id` attribute;
 * the container must be `position: relative`.
 */
export const useCardSlideAnimation = <T, E extends HTMLElement = HTMLDivElement>(
  items: T[],
  getCardId: (item: T) => string,
  {isLoading = false, settleDelay = DEFAULT_SETTLE_DELAY}: CardSlideOptions = {}
) => {
  const containerRef = useRef<E>(null)
  const lastPositions = useRef(new Map<string, CardPosition>())
  const lastRenderedCards = useRef<SlideCard<T>[]>([])
  const slideOutAnimations = useRef(new Map<string, Animation>())
  const finishedSlideOutIds = useRef(new Set<string>())
  const latestItemById = useRef(new Map<string, T>())
  const [, forceRender] = useReducer((count: number) => count + 1, 0)

  items.forEach((item) => latestItemById.current.set(getCardId(item), item))
  const shownIds = useSettledCardIds(items.map(getCardId), isLoading, settleDelay)
  const cards = addSlidingOutCards(
    shownIds.map((id) => ({id, item: latestItemById.current.get(id) as T, isSlidingOut: false})),
    lastRenderedCards.current,
    finishedSlideOutIds.current
  )

  const renderedIdsSignature = cards.map(({id}) => id).join('|')
  const lastRenderedIdsSignature = useRef(renderedIdsSignature)

  const getCardElements = () =>
    Array.from(containerRef.current?.children ?? [])
      .filter((element): element is HTMLElement => element instanceof HTMLElement && !!element.dataset.cardSlideId)
      .map((element) => ({element, id: element.dataset.cardSlideId!}))

  const savePositions = () => {
    lastPositions.current = new Map(getCardElements().map(({element, id}) => [id, getPosition(element)]))
  }

  useIsomorphicLayoutEffect(() => {
    const positionsBefore = lastPositions.current
    const shouldAnimate = canAnimate()
    const cardElements = getCardElements()
    const slidingOutIds = new Set(cards.filter(({isSlidingOut}) => isSlidingOut).map(({id}) => id))
    let startedSlideOut = false
    let skippedSlideOut = false

    finishedSlideOutIds.current.forEach((id) => {
      if (!slidingOutIds.has(id)) finishedSlideOutIds.current.delete(id)
    })

    // Slide out removed cards
    cardElements.forEach(({element, id}) => {
      const runningSlideOut = slideOutAnimations.current.get(id)

      // Card came back before it finished sliding out
      if (runningSlideOut && !slidingOutIds.has(id)) {
        runningSlideOut.cancel()
        slideOutAnimations.current.delete(id)
        element.removeAttribute('style')
        return
      }
      if (!slidingOutIds.has(id) || runningSlideOut) return

      const lastPosition = positionsBefore.get(id)
      if (!shouldAnimate || !lastPosition) {
        finishedSlideOutIds.current.add(id)
        skippedSlideOut = true
        return
      }

      startedSlideOut = true
      const animation = slideOut(element, lastPosition)
      animation.onfinish = () => {
        slideOutAnimations.current.delete(id)
        finishedSlideOutIds.current.add(id)
        forceRender()
      }
      slideOutAnimations.current.set(id, animation)
    })

    const cardsChanged = lastRenderedIdsSignature.current !== renderedIdsSignature
    lastRenderedIdsSignature.current = renderedIdsSignature

    // Reposition remaining cards, then slide in new ones
    // A card sliding out keeps its id until it finishes, so the ids alone don't show it was removed
    if ((cardsChanged || startedSlideOut) && positionsBefore.size && shouldAnimate) {
      const repositionDelay = startedSlideOut ? SLIDE_OUT_DURATION : 0
      const remainingCards = cardElements.filter(({id}) => !slidingOutIds.has(id))
      const repositionedCards = remainingCards.filter(({element, id}) => {
        const oldPosition = positionsBefore.get(id)
        return oldPosition && (oldPosition.left !== element.offsetLeft || oldPosition.top !== element.offsetTop)
      })
      const newCards = remainingCards.filter(({id}) => !positionsBefore.has(id))
      const slideInDelay = repositionDelay + (repositionedCards.length ? REPOSITION_DURATION : 0)

      repositionedCards.forEach(({element, id}) =>
        slideToNewPosition(element, positionsBefore.get(id)!, repositionDelay)
      )
      newCards.forEach(({element}) => slideIn(element, slideInDelay))
    }

    lastRenderedCards.current = cards
    savePositions()

    if (skippedSlideOut) forceRender()
  })

  // Keep saved positions accurate when the grid reflows without a re-render
  useEffect(() => {
    window.addEventListener('resize', savePositions)
    return () => {
      window.removeEventListener('resize', savePositions)
      slideOutAnimations.current.forEach((animation) => animation.cancel())
    }
  }, [])

  return {containerRef, cards}
}
